import * as React from "react"
import { CircleCheck, CircleX } from "lucide-react"
import { cn } from "@/lib/utils"
import type { CopilotCheckpoints } from "../../hooks/useCopilot"

export const CHECKPOINT_ORDER = [
    "Рассказ о компании",
    "Преимущества",
    "Подтвержден запрос",
    "Дополнительные услуги",
    "Фиксация договоренностей",
] as const

/** Tour target id for the growth-area checkpoint row. */
export const CHECKPOINT_ADDITIONAL_SERVICES_TOUR_TARGET =
    "checkpoint-additional-services"

const CHECKPOINT_TOUR_TARGETS: Partial<
    Record<(typeof CHECKPOINT_ORDER)[number], string>
> = {
    "Дополнительные услуги": CHECKPOINT_ADDITIONAL_SERVICES_TOUR_TARGET,
}

interface CheckpointListProps {
    checkpoints: CopilotCheckpoints
    /** After stop/end: unfinished checkpoints render as red CircleX. */
    showMissed?: boolean
    registerTourTarget?: (id: string, el: HTMLElement | null) => void
}

export function CheckpointList({
    checkpoints,
    showMissed = false,
    registerTourTarget,
}: CheckpointListProps) {
    const completed = CHECKPOINT_ORDER.filter((key) => checkpoints[key] === 1).length

    const rootRef = React.useCallback(
        (el: HTMLDivElement | null) => {
            registerTourTarget?.("checkpoints", el)
        },
        [registerTourTarget],
    )

    const additionalServicesRef = React.useCallback(
        (el: HTMLLIElement | null) => {
            registerTourTarget?.(CHECKPOINT_ADDITIONAL_SERVICES_TOUR_TARGET, el)
        },
        [registerTourTarget],
    )

    return (
        <div
            ref={rootRef}
            data-tour-target="checkpoints"
            className="flex h-full flex-col min-h-0"
        >
            <div className="mb-2 flex shrink-0 items-center justify-between gap-2 px-1">
                <span className="text-body2 font-medium text-neutral-text-900">Чекпоинты</span>
                <span className="text-caption text-neutral-text-500">
                    {completed}/{CHECKPOINT_ORDER.length}
                </span>
            </div>
            <ul className="flex-1 space-y-1.5 overflow-y-auto min-h-0">
                {CHECKPOINT_ORDER.map((key) => {
                    const done = checkpoints[key] === 1
                    const missed = showMissed && !done
                    const tourTarget = CHECKPOINT_TOUR_TARGETS[key]
                    return (
                        <li
                            key={key}
                            ref={
                                tourTarget === CHECKPOINT_ADDITIONAL_SERVICES_TOUR_TARGET
                                    ? additionalServicesRef
                                    : undefined
                            }
                            data-tour-target={tourTarget}
                            className="flex items-start gap-2 px-1"
                        >
                            {missed ? (
                                <CircleX
                                    className="size-4 shrink-0 mt-0.5 text-state-negative"
                                    strokeWidth={2}
                                />
                            ) : (
                                <CircleCheck
                                    className={cn(
                                        "size-4 shrink-0 mt-0.5",
                                        done ? "text-state-positive" : "text-neutral-text-500",
                                    )}
                                    strokeWidth={2}
                                />
                            )}
                            <span
                                className={cn(
                                    "text-body2",
                                    done
                                        ? "text-neutral-text-900"
                                        : missed
                                          ? "text-state-negative"
                                          : "text-neutral-text-500",
                                )}
                            >
                                {key}
                            </span>
                        </li>
                    )
                })}
            </ul>
        </div>
    )
}
